import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { enGB, pt } from 'react-day-picker/locale';
import { Controller, useForm, useWatch } from 'react-hook-form';

// Utils
import AVAILABLE_COUNTRIES from 'utils/country';
import bemify from 'utils/bemify';
import buildOrderAvailability from 'utils/order-availability';
import formatPrice from 'utils/format-price';
import getTimezoneForCountry from 'utils/country-timezone';
import { googleMapsSearchParamAddress } from 'utils/restaurant-addresses-helpers';

// Hooks
import useAllergens from 'hooks/use-allergens';

// Components
import Badge from 'components/badge';
import Button from 'components/button';
import DateTimePicker from 'components/date-time-picker';
import ErrorBox from 'components/error-box';
import Icon from 'components/icon';
import Image from 'components/image';
import Input from 'components/input';
import Select from 'components/select';
import Tooltip from 'components/tooltip';

// Constants
import {
  EMAIL_PATTERN,
  PHONE_NUMBER_PATTERN
} from 'utils/form-validation';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const RestaurantMenuOrderConfirmation = ({
  allergensTranslations = null,
  className = null,
  closedDays = [],
  isRegisteringOrder = false,
  onConfirm = () => {},
  onClose = () => {},
  orderSuccess = false,
  ordersClosingOffsetMinutes = 30,
  products = [],
  restaurantAddresses = [],
  restaurantCountry = null,
  restaurantName = null,
  schedule = [],
  selectedLocale,
  show = false,
  trackOrderUrl = null,
  translations = null
}) => {
  const addressOptions = useMemo(
    () => (Array.isArray(restaurantAddresses) ?
      restaurantAddresses
        .map((entry, index) => {
          const sanitizedAddress = typeof entry?.address === 'string' ? entry.address.trim() : '';

          if (!sanitizedAddress) return null;

          const label = typeof entry?.label === 'string' ? entry.label.trim() : '';

          return {
            address: sanitizedAddress,
            id: entry?.id || null,
            label,
            value: entry?.value || entry?.id || `address-${index}`
          };
        })
        .filter(Boolean) :
      []),
    [restaurantAddresses]
  );

  const defaultAddressValue = addressOptions[0]?.value || '';

  const {
    handleSubmit,
    formState: { errors },
    control: formControl,
    reset: resetForm,
    setValue
  } = useForm({
    defaultValues: {
      email: '',
      name: '',
      notes: '',
      phone: '',
      pickupAddressId: defaultAddressValue,
      pickupDateTime: null
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    setValue('pickupAddressId', defaultAddressValue, {
      shouldDirty: false
    });
  }, [defaultAddressValue, setValue]);

  const allAllergens = useAllergens(allergensTranslations);

  // Computed
  const totalPrice = useMemo(
    () => products.reduce((acc, product) => acc + (product.price * product.quantity), 0),
    [products]
  );

  const restaurantTimezone = useMemo(
    () => getTimezoneForCountry(restaurantCountry),
    [restaurantCountry]
  );

  // Watch the pickupDateTime field to show restaurant time
  const selectedPickupDateTime = useWatch({
    control: formControl,
    name: 'pickupDateTime'
  });

  // Check if user is in the same timezone as the restaurant
  // NOTE: resolvedOptions() is the only standard way to get the user's IANA timezone (e.g., "Europe/Lisbon")
  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const isDifferentTimezone = useMemo(() => {
    if (!userTimezone || !restaurantTimezone) return true;

    return userTimezone !== restaurantTimezone;
  }, [restaurantTimezone, userTimezone]);

  // Format the converted date for display
  const restaurantTimeDisplay = useMemo(() => {
    if (!selectedPickupDateTime || !restaurantTimezone || !isDifferentTimezone) return null;

    return selectedPickupDateTime.toLocaleString(selectedLocale, {
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
      month: 'short',
      timeZone: restaurantTimezone,
      timeZoneName: 'short',
      year: 'numeric'
    });
  }, [isDifferentTimezone, restaurantTimezone, selectedLocale, selectedPickupDateTime]);

  const pickupMinDateTime = useMemo(() => {
    if (!show) return null;

    const now = new Date();

    now.setMinutes(now.getMinutes() + 30);
    now.setSeconds(0, 0);

    return now;
  }, [show]);

  const pickupMaxDateTime = useMemo(() => {
    if (!show) return null;

    const limit = new Date();

    limit.setDate(limit.getDate() + 14);
    limit.setHours(23, 45, 0, 0);

    return limit;
  }, [show]);

  // Restrict pickup selection to the restaurant's opening hours, excluding closed
  // days and the configured closing-time offset (minutes before closing).
  const orderAvailability = useMemo(
    () => buildOrderAvailability({
      closedDays,
      offsetMinutes: ordersClosingOffsetMinutes,
      schedule
    }),
    [closedDays, ordersClosingOffsetMinutes, schedule]
  );

  // Handlers
  const handleFormSubmit = (data) => {
    const selectedAddress = addressOptions.find(address => address.value === data.pickupAddressId) ||
      addressOptions[0] ||
      null;

    onConfirm({
      ...data,
      products,
      resetForm,
      restaurantAddress: selectedAddress ?
        {
          address: selectedAddress.address,
          id: selectedAddress.id,
          label: selectedAddress.label
        } :
        null,
      restaurantTimezone
    });
  };

  if (!show) return null;

  const orderFormContent = (
    <form
      className={styles.restaurantMenuOrderConfirmation__form}
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <div className={styles.restaurantMenuOrderConfirmation__formFields}>
        <Controller
          control={formControl}
          name="name"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              autoFocus
              description={errors.name?.message}
              disabled={isRegisteringOrder}
              label={translations?.fields?.name?.label}
              name="name"
              placeholder={translations?.fields?.name?.placeholder}
              required
              validationState={errors.name?.message ? 'error' : null}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
          rules={{
            required: translations?.fields?.name?.validation?.required
          }}
        />
        <Controller
          control={formControl}
          name="phone"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              description={errors.phone?.message}
              disabled={isRegisteringOrder}
              label={translations?.fields?.phone?.label}
              name="phone"
              placeholder={translations?.fields?.phone?.placeholder}
              required
              type="tel"
              validationState={errors.phone?.message ? 'error' : null}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
          rules={{
            pattern: {
              message: translations?.fields?.phone?.validation?.pattern,
              value: PHONE_NUMBER_PATTERN
            },
            required: translations?.fields?.phone?.validation?.required
          }}
        />
        <Controller
          control={formControl}
          name="email"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              description={errors.email?.message}
              disabled={isRegisteringOrder}
              label={translations?.fields?.email?.label}
              name="email"
              placeholder={translations?.fields?.email?.placeholder}
              required
              type="email"
              validationState={errors.email?.message ? 'error' : null}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
          rules={{
            pattern: {
              message: translations?.fields?.email?.validation?.pattern,
              value: EMAIL_PATTERN
            },
            required: translations?.fields?.email?.validation?.required
          }}
        />
        {!!addressOptions.length && addressOptions.length > 1 && (
          <Controller
            control={formControl}
            name="pickupAddressId"
            render={({ field: { onBlur, onChange, value } }) => (
              <Select
                className={styles.restaurantMenuOrderConfirmation__addressField}
                disabled={isRegisteringOrder}
                id="pickup-location"
                label={translations?.fields?.pickupAddress?.label}
                native
                options={addressOptions.map(option => ({
                  text: option.label ?
                    `${option.label} — ${option.address}` :
                    option.address,
                  value: option.value
                }))}
                placeholder={translations?.fields?.pickupAddress?.placeholder}
                required
                selectedOption={value}
                validationState={errors.pickupAddressId?.message ? 'error' : null}
                onBlur={onBlur}
                onChange={onChange}
              />
            )}
            rules={{
              required: translations?.fields?.pickupAddress?.validation?.required
            }}
          />
        )}
        <Controller
          control={formControl}
          name="pickupDateTime"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimePicker
              disabled={isRegisteringOrder}
              filterDate={orderAvailability.isConstrained ? orderAvailability.isDateAvailable : null}
              isTimeAllowed={orderAvailability.isConstrained ? orderAvailability.isTimeAvailable : null}
              label={translations?.fields?.pickupDateTime?.label}
              locale={selectedLocale === 'pt-PT' ? pt : enGB}
              maxDateTime={pickupMaxDateTime}
              minDateTime={pickupMinDateTime}
              name="pickupDateTime"
              placeholder={translations?.fields?.pickupDateTime?.placeholder}
              required
              timeInterval={15}
              validationError={errors.pickupDateTime?.message}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
          rules={{
            required: translations?.fields?.pickupDateTime?.validation?.required,
            validate: {
              futureDate: (value) => {
                if (!value) return translations?.fields?.pickupDateTime?.validation?.required;

                const selectedTimestamp = value?.getTime?.() ?? new Date(value).getTime();

                if (Number.isNaN(selectedTimestamp)) {
                  return translations?.fields?.pickupDateTime?.validation?.invalid;
                }

                if (pickupMinDateTime && selectedTimestamp < pickupMinDateTime.getTime()) {
                  return translations?.fields?.pickupDateTime?.validation?.future;
                }

                if (pickupMaxDateTime && selectedTimestamp > pickupMaxDateTime.getTime()) {
                  return translations?.fields?.pickupDateTime?.validation?.range;
                }

                if (orderAvailability.isConstrained && !orderAvailability.isTimeAvailable(new Date(selectedTimestamp))) {
                  return translations?.fields?.pickupDateTime?.validation?.outsideHours;
                }

                return true;
              }
            }
          }}
        />
        {restaurantTimeDisplay && translations?.fields?.pickupDateTime?.restaurantTimeNote && (
          <ErrorBox
            message={translations.fields.pickupDateTime.restaurantTimeNote
              .replace('{{restaurantTime}}', restaurantTimeDisplay)
              .replace('{{restaurantCountry}}', AVAILABLE_COUNTRIES[restaurantCountry] || restaurantCountry)}
          />
        )}
        <Controller
          control={formControl}
          name="notes"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              description={errors.notes?.message}
              disabled={isRegisteringOrder}
              label={translations?.fields?.notes?.label}
              maxLength={500}
              minRows={3}
              name="notes"
              placeholder={translations?.fields?.notes?.placeholder}
              type="textarea"
              validationState={errors.notes?.message ? 'error' : null}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
          rules={{
            validate: (value) => {
              if (!value) return true;

              if (typeof value !== 'string') {
                return translations?.fields?.notes?.validation?.invalid;
              }

              if (value.length > 500) {
                return translations?.fields?.notes?.validation?.maxLength;
              }

              return true;
            }
          }}
        />
        {!!addressOptions.length && addressOptions.length === 1 && (
          <>
            <div className={styles.restaurantMenuOrderConfirmation__divider} />
            <div>
              <label className={styles.restaurantMenuOrderConfirmation__defaultAddressLabel}>
                {translations?.fields?.pickupAddress?.label}
              </label>
              <Button
                className={styles.restaurantMenuOrderConfirmation__defaultAddressButton}
                contentClassName={styles.restaurantMenuOrderConfirmation__defaultAddressButtonContent}
                external
                href={`https://www.google.com/maps/place/${googleMapsSearchParamAddress(addressOptions[0]?.address)}`}
                icon="external-link"
                iconPosition="left"
                iconSize="l"
                noBackground
                paddingSize="none"
                size="s"
                text={addressOptions[0]?.address}
                textWeight="regular"
                theme="unstyled"
              />
            </div>
          </>
        )}
      </div>
      <Button
        className={styles.restaurantMenuOrderConfirmation__confirmButton}
        loading={isRegisteringOrder}
        text={translations?.confirmButtonLabel}
        type="submit"
      />
    </form>
  );

  const orderSuccessContent = (
    <div className={styles.restaurantMenuOrderConfirmation__success}>
      <div className={styles.restaurantMenuOrderConfirmation__successContent}>
        <Icon
          className={styles.restaurantMenuOrderConfirmation__successIcon}
          name="check-circle--green"
        />
        <h2 className={styles.restaurantMenuOrderConfirmation__successTitle}>
          {translations?.success?.title}
        </h2>
        <p className={styles.restaurantMenuOrderConfirmation__successMessage}>
          {translations?.success?.message.replace('{{restaurantName}}', restaurantName)}
        </p>
        {!!trackOrderUrl && (
          <Button
            className={styles.restaurantMenuOrderConfirmation__successButton}
            external
            href={trackOrderUrl}
            text={translations?.success?.trackButton}
            theme="primary"
          />
        )}
        <Button
          className={styles.restaurantMenuOrderConfirmation__successButton}
          text={translations?.success?.returnButton}
          theme="grey"
          onClick={onClose}
        />
      </div>
    </div>
  );

  const orderContent = (
    <div className={styles.restaurantMenuOrderConfirmation__order}>
      <div className={`${styles.restaurantMenuOrderConfirmation__orderContainer} ${styles.restaurantMenuOrderConfirmation__orderContainer_greyBackground}`}>
        <div className={`${styles.restaurantMenuOrderConfirmation__orderContent} ${styles.restaurantMenuOrderConfirmation__orderContent_alignRight}`}>
          <div>
            {!orderSuccess && (
              <Button
                className={styles.restaurantMenuOrderConfirmation__returnButton}
                icon="chevron-left"
                iconPosition="left"
                text={translations?.returnButtonLabel}
                theme="grey"
                onClick={onClose}
              />
            )}
            <ul className={styles.restaurantMenuOrderConfirmation__orderProducts}>
              {products.map((product) => {
                const productTitle = (
                  product?.translations?.[selectedLocale]?.title ||
              product?.fallbackTranslations?.title
                );

                return (
                  <li
                    key={product.entityId}
                    className={styles.restaurantMenuOrderConfirmation__product}
                  >
                    {!!product.imageUrl && (
                      <div className={styles.restaurantMenuOrderConfirmation__productImageContainer}>
                        <Image
                          alt={productTitle}
                          className={styles.restaurantMenuOrderConfirmation__productImage}
                          layout="fill"
                          objectFit="cover"
                          sizes="120px"
                          src={product.imageUrl}
                        />
                        {product.quantity > 1 && (
                          <Badge
                            className={styles.restaurantMenuOrderConfirmation__productQuantity}
                            text={product.quantity}
                          />
                        )}
                      </div>
                    )}
                    <div className={styles.restaurantMenuOrderConfirmation__productDetails}>
                      <div className={styles.restaurantMenuOrderConfirmation__productHeader}>
                        <h6 className={styles.restaurantMenuOrderConfirmation__productTitle}>
                          {productTitle}
                        </h6>
                        <span className={styles.restaurantMenuOrderConfirmation__productPrice}>
                          {formatPrice(product.price * product.quantity)}
                        </span>
                      </div>
                      <ul className={styles.restaurantMenuOrderConfirmation__productAllergens}>
                        {(Array.isArray(product.allergens) ? product.allergens : []).map((entry) => {
                          const allergenDetails = allAllergens.find(allergen => allergen.id === entry);

                          if (!allergenDetails) return null;

                          return (
                            <li key={entry}>
                              <Tooltip
                                text={allergenDetails.label}
                                trigger={(
                                  <div>
                                    <Icon
                                      className={styles.restaurantMenuOrderConfirmation__productAllergenIcon}
                                      name={allergenDetails.icon}
                                      size="small"
                                    />
                                  </div>
                                )}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <footer className={styles.restaurantMenuOrderConfirmation__orderFooter}>
            <span className={styles.restaurantMenuOrderConfirmation__orderTotalLabel}>
              {`${translations?.totalLabel}:`}
            </span>
            <span className={styles.restaurantMenuOrderConfirmation__orderTotal}>
              {formatPrice(totalPrice)}
            </span>
          </footer>
        </div>
      </div>
      <div className={styles.restaurantMenuOrderConfirmation__orderContainer}>
        <div className={styles.restaurantMenuOrderConfirmation__orderContent}>
          {orderSuccess ? orderSuccessContent : orderFormContent}
        </div>
      </div>
    </div>
  );

  return (
    <section className={`${styles.restaurantMenuOrderConfirmation}${className ? ` ${className}` : ''}`}>
      <div className={styles.restaurantMenuOrderConfirmation__content}>
        {orderContent}
      </div>
    </section>
  );
};

RestaurantMenuOrderConfirmation.propTypes = {
  allergensTranslations: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  closedDays: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.instanceOf(Date)
  })),
  isRegisteringOrder: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  orderSuccess: PropTypes.bool,
  ordersClosingOffsetMinutes: PropTypes.number,
  products: PropTypes.arrayOf(PropTypes.shape({
    entityId: PropTypes.string,
    imageUrl: PropTypes.string,
    price: PropTypes.number,
    quantity: PropTypes.number,
    title: PropTypes.string
  })),
  restaurantAddresses: PropTypes.arrayOf(PropTypes.shape({
    address: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string
  })),
  restaurantCountry: PropTypes.string,
  restaurantName: PropTypes.string,
  schedule: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string,
    timeEntries: PropTypes.arrayOf(PropTypes.shape({
      closeTime: PropTypes.string,
      openTime: PropTypes.string
    }))
  })),
  selectedLocale: PropTypes.string,
  show: PropTypes.bool,
  trackOrderUrl: PropTypes.string,
  translations: PropTypes.shape({
    confirmButtonLabel: PropTypes.string,
    fields: PropTypes.shape({
      email: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        validation: PropTypes.shape({
          pattern: PropTypes.string,
          required: PropTypes.string
        })
      }),
      name: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        validation: PropTypes.shape({
          required: PropTypes.string
        })
      }),
      notes: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        validation: PropTypes.shape({
          invalid: PropTypes.string,
          maxLength: PropTypes.string
        })
      }),
      phone: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        validation: PropTypes.shape({
          pattern: PropTypes.string,
          required: PropTypes.string
        })
      }),
      pickupAddress: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        singleLocationLabel: PropTypes.string,
        validation: PropTypes.shape({
          required: PropTypes.string
        })
      }),
      pickupDateTime: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        restaurantTimeNote: PropTypes.string,
        validation: PropTypes.shape({
          future: PropTypes.string,
          invalid: PropTypes.string,
          outsideHours: PropTypes.string,
          range: PropTypes.string,
          required: PropTypes.string
        })
      })
    }),
    returnButtonLabel: PropTypes.string,
    success: PropTypes.shape({
      message: PropTypes.string,
      returnButton: PropTypes.string,
      title: PropTypes.string,
      trackButton: PropTypes.string
    }),
    totalLabel: PropTypes.string
  })
};


export default RestaurantMenuOrderConfirmation;
